import { ColDef } from 'ag-grid-community';
import { IconProps } from 'components/ui';
import { ChangeEvent, useMemo } from 'react';
import { PATHS } from 'settings/config';
import {
  ListingBookmarkTypeEnum,
  ListingOrgWritePublishActionEnum,
  ListingOrgWriteRequest,
  ListingTypeEnum,
  NetworkBasic,
  OrgNetworks,
  ProductTypes,
} from './api/generated';

export interface ColumnDef extends ColDef {
  field: string;
  width?: number;
  editable?: boolean;
  order?: number;
}

export interface INetworks {
  networks: OrgNetworks[];
}

export const productType: {
  id: ProductTypes;
  title: string;
  icon: IconProps['component'];
}[] = [
  {
    id: ProductTypes.Ct,
    title: 'Cattle',
    icon: 'CattleIcon',
  },
  {
    id: ProductTypes.Sh,
    title: 'Sheep',
    icon: 'SheepIcon',
  },
  {
    id: ProductTypes.Gt,
    title: 'Goats',
    icon: 'GoatIcon',
  },
];

export const priceType: {
  id: string;
  title: string;
}[] = [
  {
    id: 'PER_HEAD',
    title: 'Per Head',
  },
  {
    id: 'HSCW',
    title: 'HSCW',
  },
  {
    id: 'LIVEWEIGHT',
    title: 'Liveweight',
  },
];
export const inputType = {
  CHECKBOX: 'checkbox',
  RADIO: 'radio',
  INPUT: 'input',
  SELECT: 'select',
  MULTISELECT: 'multiselect',
};
export const states: string[] = [
  'ACT',
  'NSW',
  'NT',
  'QLD',
  'SA',
  'TAS',
  'VIC',
  'WA',
];

// Single-select: '' clears the filter (the "All" default), matching the other radio filters.
export const soldStatus: {
  label: string;
  value: string;
}[] = [
  { label: 'All', value: '' },
  { label: 'Filled', value: 'true' },
  { label: 'Open', value: 'false' },
];

// `auction=true` shows only auctions, `auction=false` hides them, '' clears the filter.
export const auctionStatus: {
  label: string;
  value: string;
}[] = [
  { label: 'All', value: '' },
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

export const types: {
  label: string;
  value: string;
}[] = [
  { label: 'Wanted', value: 'BUY' },
  { label: 'For Sale', value: 'SELL' },
];

// Keep in step with the `stock_category` options in
// components/marketplace/listing/v2/steps/listing-form.json.
export const stockCategory: string[] = [
  'Breeding',
  'Trade',
  'Store',
  'Stud',
  'Live Export',
  'Feeder',
  'Restocker',
  'Processor',
  'Airfreight',
];

// TODO mock network data this will need to come from the API
export const networks = [
  {
    org: 'Elders',
    networks: [
      {
        id: 'AEF453',
        name: 'Sheep - WA',
      },
      {
        id: 'BEF454',
        name: 'Sheep - SA',
      },
    ],
  },
  {
    org: 'Agora',
    networks: [
      {
        id: 'CEF456',
        name: 'Sheep - WA',
      },
    ],
  },
];

export enum ListingVisibilityFilterEnum {
  Private = 'PRIVATE',
  Public = 'PUBLIC',
  Archived = 'ARCHIVED',
  Draft = 'HIDDEN',
}

export enum ListingPublicStatusFilterEnum {
  Review = 'REVIEW',
  Rejected = 'REJECTED',
  Approved = 'APPROVED',
}

export const addAllItem = (arr: { id: string; value: string }[]) => {
  const allItem = { id: 'all', value: 'All' };
  return [allItem, ...arr];
};

export const listingVisibilityFilterItems = addAllItem(
  Object.entries(ListingVisibilityFilterEnum).map(([label, value]) => ({
    id: value,
    value: label,
  }))
);

export const listingStatusItems = addAllItem(
  Object.entries(ListingPublicStatusFilterEnum).map(([label, value]) => ({
    id: value,
    value: label,
  }))
);

export interface IFilter {
  product?: ProductTypes;
  state?: string[];
  quantityMin?: string;
  quantityMax?: string;
  priceType?: string;
  priceMin?: string;
  priceMax?: string;
  search?: string;
  sold?: string;
  auction?: string;
  type?: ListingTypeEnum;
  stockCat?: string[];
  networks?: string[];
  public_status?: string[];
  visibility?: ListingVisibilityFilterEnum;
  bookmark?: ListingBookmarkTypeEnum | null;
}

// Filters that the filter dialog treats as multi-select checkbox groups.
export const ARRAY_FILTER_KEYS = [
  'state',
  'stockCat',
  'networks',
  'public_status',
  'visibility',
] as const;

/**
 * Coerce a URL query value into a string array. `router.query` yields a
 * string for a single value (`?state=QLD`) and `useUrlFilters().setFilter`
 * writes comma-joined strings (`?state=NSW,QLD`), so both must be split.
 */
export function toFilterArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => toFilterArray(item));
  }
  if (typeof value === 'string') {
    return value.split(',').filter(Boolean);
  }
  return [];
}

/**
 * Normalise every multi-select filter in `filters` to a string array,
 * dropping any that end up empty.
 */
export function normaliseFilterArrays(filters: Record<string, any>): IFilter {
  const normalised: Record<string, any> = { ...filters };
  ARRAY_FILTER_KEYS.forEach((key) => {
    if (!(key in normalised)) return;
    const values = toFilterArray(normalised[key]);
    if (values.length) {
      normalised[key] = values;
    } else {
      delete normalised[key];
    }
  });
  return normalised as IFilter;
}

export function hasValue(value: string | undefined): boolean {
  return value !== undefined;
}

export const useBadgeCount = (stateFilter: IFilter) => {
  const badgeCount = useMemo(
    () => stateFilter && Object.keys(stateFilter).length,
    [stateFilter]
  );

  return badgeCount;
};

export const handleValueChange = (
  event: ChangeEvent<HTMLInputElement>,
  type: string,
  stateFilter: IFilter,
  setStateFilter: (filter: IFilter) => void
) => {
  const { name, value, checked } = event.target;
  if (type === inputType.INPUT) {
    let updatedFilter: IFilter = {
      ...stateFilter,
      [name]: value,
    };
    if (value === '' || value === undefined) {
      delete updatedFilter[name as keyof IFilter];
    }
    setStateFilter(updatedFilter);
  }

  if (type === inputType.RADIO) {
    let updatedFilter: IFilter = {
      ...stateFilter,
      [name]: value,
    };
    if (
      stateFilter[name as keyof IFilter] ===
      updatedFilter[name as keyof IFilter]
    ) {
      delete updatedFilter[name as keyof IFilter];
    }

    if (value === '' || value === undefined) {
      delete updatedFilter[name as keyof IFilter];
    }
    setStateFilter(updatedFilter);
  }

  if (type === inputType.CHECKBOX) {
    if (checked) {
      setStateFilter({
        ...stateFilter,
        [name]: Array.isArray(stateFilter[name as keyof IFilter])
          ? [...(stateFilter[name as keyof IFilter] as string[]), value]
          : [value],
      });
    } else {
      const updatedArray = (
        stateFilter[name as keyof IFilter] as string[]
      ).filter((item) => item !== value);

      const updatedFilter: IFilter = {
        ...stateFilter,
        [name]: updatedArray,
      };

      if (updatedArray.length === 0) {
        delete updatedFilter[name as keyof IFilter];
      }

      setStateFilter(updatedFilter);
    }
  }
};

export const handleSelectValueChange = (
  name: string,
  selected: any,
  type: string,
  stateFilter: IFilter,
  setStateFilter: (filter: IFilter) => void
) => {
  if (type === inputType.SELECT) {
    let updatedFilter: IFilter = {
      ...stateFilter,
      [name]: selected?.id,
    };
    if (updatedFilter[name as keyof IFilter] === null) {
      delete updatedFilter[name as keyof IFilter];
    }

    if (
      stateFilter[name as keyof IFilter] ===
      updatedFilter[name as keyof IFilter]
    ) {
      delete updatedFilter[name as keyof IFilter];
    }

    if (selected === '' || selected === undefined) {
      delete updatedFilter[name as keyof IFilter];
    }
    if (selected?.id === 'all') {
      delete updatedFilter[name as keyof IFilter];
    }
    setStateFilter(updatedFilter);
  }

  if (type === inputType.MULTISELECT) {
    const updatedFilter: IFilter = {
      ...stateFilter,
      [name as keyof IFilter]: selected,
    };

    Object.keys(updatedFilter).forEach((key) => {
      if (
        // `sold`/`auction` are single-select, so they are deliberately not split into arrays.
        typeof updatedFilter[key as keyof IFilter] === 'string' &&
        (key === 'state' ||
          key === 'stockCat' ||
          key === 'networks' ||
          key === 'public_status')
      ) {
        updatedFilter[key] = (
          updatedFilter[key as keyof IFilter] as string
        ).split(',');
      } else if (updatedFilter[key as keyof IFilter] === undefined) {
        delete updatedFilter[key as keyof IFilter];
      }
    });

    // Clear filter if 'all' is selected or no selection is made
    if (
      (selected?.length > 0 && selected.includes('all')) ||
      selected.length === 0
    ) {
      delete updatedFilter[name as keyof IFilter];
    }

    setStateFilter(updatedFilter);
  }
};

export function getListOfNetworks(data: INetworks): NetworkBasic[] {
  const allNetworks: NetworkBasic[] = [];
  data.networks.forEach((organisation: OrgNetworks) => {
    organisation.networks.forEach((network: NetworkBasic) => {
      allNetworks.push(network);
    });
  });
  return allNetworks;
}

export function getHeadNetworks(networks?: OrgNetworks[]): NetworkBasic[] {
  const allNetworks: NetworkBasic[] = [];
  if (!networks) return allNetworks;
  networks.forEach((organisation: OrgNetworks) => {
    organisation.networks.forEach((network: NetworkBasic) => {
      if (!network.org && network.head) {
        allNetworks.push(network);
      }
    });
  });
  return allNetworks;
}

export function getMyNetworks(
  networks?: OrgNetworks[],
  orgId?: string
): NetworkBasic[] {
  const allNetworks: NetworkBasic[] = [];
  if (!networks || !orgId) return allNetworks;

  networks?.forEach((organisation: OrgNetworks) => {
    if (organisation.id === orgId) {
      organisation.networks.forEach((network: NetworkBasic) => {
        if (network.org) {
          allNetworks.push(network);
        }
      });
    }
  });
  return allNetworks;
}

export function getListOfNetworksId(networks: OrgNetworks[]): string[] {
  const allNetworks: string[] = [];
  if (!networks) return allNetworks;
  networks.forEach((organisation: OrgNetworks) => {
    organisation.networks.forEach((network: NetworkBasic) => {
      allNetworks.push(network.id);
    });
  });

  return allNetworks;
}

/**
 * Handles the logic of selecting and deselecting "Network Only Listings" vs explicit networks.
 */
export function calculateNetworkFilterValues(
  newSelectedIds: string[],
  effectiveNetworksValue: string[],
  myNetworksIds: string[]
): string[] {
  const isAllListingsToggledOn =
    !effectiveNetworksValue.includes('all-listings') &&
    newSelectedIds.includes('all-listings');
  if (isAllListingsToggledOn) {
    return ['all-listings'];
  }

  if (newSelectedIds.includes('all-listings') && newSelectedIds.length > 1) {
    newSelectedIds = newSelectedIds.filter((id) => id !== 'all-listings');
  }

  const isNetworkOnlyToggledOn =
    !effectiveNetworksValue.includes('all') && newSelectedIds.includes('all');
  const isNetworkOnlyToggledOff =
    effectiveNetworksValue.includes('all') && !newSelectedIds.includes('all');

  if (isNetworkOnlyToggledOn) {
    return ['all'];
  } else if (isNetworkOnlyToggledOff) {
    return [];
  } else {
    const allMyNetworksSelected =
      myNetworksIds.length > 0 &&
      myNetworksIds.every((id) => newSelectedIds.includes(id));

    if (allMyNetworksSelected) {
      if (!newSelectedIds.includes('all')) {
        newSelectedIds = ['all'];
      }
    } else {
      if (newSelectedIds.includes('all')) {
        newSelectedIds = newSelectedIds.filter((id) => id !== 'all');
      }
    }
  }

  return newSelectedIds;
}

// Coerce a form date value (a Date or an ISO string) to a `YYYY-MM-DD` string for the API date fields.
const toDateOnly = (value: unknown): string | null => {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return String(value).slice(0, 10);
};

export const transformFormToPayload = (
  data: any,
  publishAction: ListingOrgWritePublishActionEnum
): ListingOrgWriteRequest => {
  const priceType = data.price_type;
  const pricingOption = data.pricing_option;

  const prices = data.prices || {};

  if (pricingOption === 'range') {
    if (data.temp_prices_range_min) {
      prices[priceType] = {
        min: data.temp_prices_range_min,
        max: data.temp_prices_range_max,
      };
    }
  } else {
    // fixed, negotiable, poa
    if (data.temp_prices_single_min) {
      prices[priceType] = {
        min: data.temp_prices_single_min,
        max: data.temp_prices_single_min,
      };
    }
  }

  const formData = { ...data };

  // Extract top-level fields
  const owner = formData.owner;
  const title = formData.title;
  const product_type = formData.product_type;
  // The edit form seeds an empty-string address object when the listing has none; the API rejects
  // any address without an id, so only send one that's actually selected.
  const listing_address = formData.listing_address?.id
    ? formData.listing_address
    : undefined;
  const anonymous = formData.anonymous;
  const quantity = formData.quantity;
  const networks = formData.networks;
  const head_networks = formData.head_networks;
  const type = formData.type;
  const price_point = formData.price_point;

  // Cleanup temporary fields
  delete formData.temp_prices_range_min;
  delete formData.temp_prices_range_max;
  delete formData.temp_prices_single_min;
  delete formData.temp_prices;

  // Cleanup top-level fields from form_data to avoid duplication
  delete formData.owner;
  delete formData.title;
  delete formData.product_type;
  delete formData.listing_address;
  delete formData.anonymous;
  delete formData.quantity;
  delete formData.networks;
  delete formData.head_networks;
  delete formData.type;
  delete formData.publish_action;
  delete formData.prices;
  delete formData.media;
  delete formData.price;
  delete formData.pricing_option;
  delete formData.price_type;
  delete formData.listing_name;
  delete formData.quantity_min;
  delete formData.quantity_max;

  // Auction listings capture a single Auction Date instead of an availability range. Mirror it onto
  // availability_from (the key existing admin/Slack surfaces read) and drop the range's "to" value.
  if (pricingOption === 'auction') {
    formData.availability_from = data.auction_date ?? null;
    delete formData.availability_to;
  }

  // First-class fields the backend derives the `price_method` / `date` columns from. Auction listings
  // use their single Auction Date as both bounds; other listings use the availability range. The legacy
  // form_data keys above are still sent and kept in sync by the backend for older clients.
  const dateFromSource =
    pricingOption === 'auction' ? data.auction_date : data.availability_from;
  const dateToSource =
    pricingOption === 'auction' ? data.auction_date : data.availability_to;

  // price_method is upper-case on the API (like `type` and `price_point`); the form's pricing_option
  // — and the legacy form_data.negotiable_price above — stay lower-case.
  const priceMethod = pricingOption
    ? String(pricingOption).toUpperCase()
    : null;

  const listingPayload: ListingOrgWriteRequest = {
    owner: owner ?? undefined,
    title: title ?? 'Untitled Listing',
    form_data: {
      ...formData,
      negotiable_price: pricingOption,
      price_point: price_point,
    },
    product: product_type,
    price_method: priceMethod,
    date_from: toDateOnly(dateFromSource),
    date_to: toDateOnly(dateToSource),
    listing_address: listing_address,
    anonymous: anonymous === 'true',
    quantity_min: quantity ? Number(quantity) : null,
    quantity_max: quantity ? Number(quantity) : null,
    media: data.media || [],
    networks: Array.from(
      new Set([...(networks || []), ...(head_networks || [])])
    ),
    prices: prices,
    type: type as ListingTypeEnum,
    publish_action: publishAction,
  };

  return listingPayload;
};

export const getPostSubmitRedirectPath = (
  publishAction: ListingOrgWritePublishActionEnum,
  listingType: ListingTypeEnum,
  isApp: boolean,
  listingId?: string
): string => {
  if (publishAction === ListingOrgWritePublishActionEnum.Draft) {
    if (isApp) {
      return listingType === ListingTypeEnum.Buy
        ? PATHS.app.myBuyListings
        : PATHS.app.mySellListings;
    } else {
      return listingType === ListingTypeEnum.Buy
        ? PATHS.marketplace.buyListings
        : PATHS.marketplace.sellListings;
    }
  } else {
    if (listingId) {
      // view the listing or confirmation
      return `${PATHS.marketplace.viewListings}/${listingId}`;
    }
    // Fallback if no ID (shouldn't happen for public listing success)
    return PATHS.marketplace.listings;
  }
};
